import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses, sortObjectToList } from 'utils/utils';
import { getSeoFooterMessaging } from 'src_overrides/studios/universal-pictures-cms/utils/seoFooterMessaging';

import Button from 'components/button/button';
import { CardList, CardListItem } from 'components/cardList/cardList';
import InheritIndicator from 'components/editor/settings/inheritIndicator';
import LinkField from 'components/editor/settings/linkField';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import GoToMediaButton from 'components/media/goToMediaButton';

import MovieFooterEditor from 'components/editor/movies/movieFooter/movieFooter';

import s from 'components/editor/movies/movieFooter/movieFooterUniversal.sss';

export default class UniversalMovieFooterEditor extends MovieFooterEditor {
	s = s;

	constructor(props) {
		super(props);

		this.addItem = this.addItem.bind(this);
		this.deleteItem = this.deleteItem.bind(this);
		this.onReorder = this.onReorder.bind(this);
		this.renderLogo = this.renderLogo.bind(this);

		this.migrateLegacyFooterData();
		this.removeDeletedInheritedData();
	}

	migrateLegacyFooterData() {
		const { copyright, links } = jsonPointer.get(Store.get().data, this.footerPath) || {};

		if (copyright && Array.isArray(copyright)) {
			const newCopyright = Object.fromEntries(copyright.map((line, i) => [crypto.randomUUID(), { ...line, order: i }]));
			jsonPointer.setDeep(Store.get().data, `${this.footerPath}/copyright`, newCopyright);
		}

		if (links && Array.isArray(links)) {
			const newLinks = Object.fromEntries(links.map((link, i) => [crypto.randomUUID(), { ...link, order: i }]));
			jsonPointer.setDeep(Store.get().data, `${this.footerPath}/links`, newLinks);
		}
	}

	removeDeletedInheritedData() {
		const inheritBasePath = '/navigation/content/moviePageFooter';
		const inheritedLinks = jsonPointer.get(Store.get().data, `${inheritBasePath}/links`) || {};
		Object.entries(inheritedLinks)
			.filter(([, link]) => link === null)
			.forEach(([linkKey,]) => {
				jsonPointer.removeDeep(Store.get().data, `${this.footerPath}/links/${linkKey}`);
			});

		const inheritedCopyright = jsonPointer.get(Store.get().data, `${inheritBasePath}/copyright`) || {};
		Object.entries(inheritedCopyright)
			.filter(([, line]) => line === null)
			.forEach(([lineKey,]) => {
				jsonPointer.removeDeep(Store.get().data, `${this.footerPath}/copyright/${lineKey}`);
			});
	}

	// ========== FOOTER METHODS ==========

	onReorder(e) {
		const allItems = [...e.target.querySelectorAll('[data-path]')].map(el => el.dataset.path);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);
		allItems.forEach((path, i) => {
			jsonPointer.setDeep(Store.get().data, `${this.footerPath}${path}/order`, i + 1);
		});
	}

	addItem(path, data = { name: 'New item', destination: '' }) {
		const id = crypto.randomUUID();
		const currentLinks = jsonPointer.get(this.movie.footer, path) || {};
		const maxOrder = Math.max(0, ...Object.values(currentLinks).map(link => link.order || 0));
		jsonPointer.setDeep(Store.get().data, `${this.footerPath}${path}/${id}`, { ...data, order: maxOrder + 1 });
	}

	async deleteItem(path) {
		const ok = await confirm('This will remove the item from the footer.', {
			title: 'Delete item?',
			danger: true,
			button: 'Delete',
		});
		if (!ok) return;

		const isInherited = jsonPointer.has(Store.get().data, `/navigation/content/moviePageFooter${path}`);
		if (isInherited) {
			// If the item is inherited, we set the id to null to hide it
			jsonPointer.setDeep(Store.get().data, `${this.footerPath}${path}`, null);
		} else {
			// If the item is not inherited, we remove it from the footer data entirely
			jsonPointer.removeDeep(Store.get().data, `${this.footerPath}${path}`);
		}

		this.props.select('/footer');
	}

	// ========= RENDER VISUAL METHODS ==========

	renderLogo(logo) {
		return (
			<div class={s.logo} key={logo.id}>
				<img src={logo.url} />
			</div>
		);
	}

	renderVisual() {
		const footer = this.movie.footer || {};

		const logos = sortObjectToList(footer.logos).filter(logo => logo.url);
		const links = sortObjectToList(footer.links).filter(link => !link._hidden);
		const copyright = sortObjectToList(footer.copyright).filter(line => !line._hidden);
		const seoMessage = footer.seoMessaging || getSeoFooterMessaging(this.movie, this.state.thundrData?.copy?.baseCopy);

		return (
			<SelectableSection {...this.props} class={joinClasses(s.wrapper, this.props.class)} section="/footer">
				<div class={s.footerContent}>
					<div class={s.top}>
						{logos.length > 0 && (
							<div class={s.logos}>
								{logos.map((logo) => this.renderLogo(logo))}
							</div>
						)}
					</div>
					{links.length > 0 && (
						<div class={s.links}>
							{links.map((link, i) => (
								<SelectableSection {...this.props} class={s.link} key={link.id || i} section={`/footer/links/${link.id || i}`}>{link.name}</SelectableSection>
							))}
						</div>
					)}
					{(copyright.length > 0 || seoMessage) && (
						<div class={s.legal}>
							{copyright.map((line, i) => (
								/* The first line (Powster) is not selectable, the rest are */
								<SelectableSection {...this.props} key={line.id || i} section={i !== 0 && `/footer/copyright/${line.id || i}`}>{line.name.replace(/\[Year\]/gi, this.copyrightYear)}</SelectableSection>
							))}
							{seoMessage && <SelectableSection {...this.props} key="seoMessaging" section="/footer/seoMessaging">{seoMessage}</SelectableSection>}
						</div>
					)}
				</div>
			</SelectableSection>
		);
	}

	// ======== RENDER SETTINGS METHODS ==========

	renderFooterLinkSettings(linkId) {
		const readonly = !canUser('write');
		const path = `${this.footerPath}/links/${linkId}`;
		const inheritPath = `/navigation/content/moviePageFooter/links/${linkId}`;
		const link = this.movie?.footer?.links?.[linkId];
		const privacy = link.destination === 'cookies';

		const moviePageOptions = [
			{ name: 'Synopsis', destination: 'boltSynopsis' },
			{ name: 'Gallery', destination: 'boltGallery' },
			{ name: 'Videos', destination: 'boltVideos' },
			{ name: 'Showtimes', destination: 'boltShowtimes' },
			{ name: 'Watch at Home', destination: 'boltHomeEnt' },
		];

		return (
			<div class={s.itemEditor}>
				<SimpleField dark label="Label text" path={`${path}/name`} inheritPath={`${inheritPath}/name`} inheritType="locale" disabled={readonly || privacy} />
				{privacy
					? <SimpleField dark label="Target" placeholder="Cookie consent (managed by region)" inheritType="locale" forceInherits disabled readonly />
					: <LinkField dark label="Target" path={`${path}/destination`} options={moviePageOptions} inheritPath={`${inheritPath}/destination`} inheritType="locale" disabled={readonly} />
				}
				<div class={s.buttonRow}>
					<Button dark text danger disabled={readonly} onClick={() => this.deleteItem(`/links/${linkId}`)}>
						Delete link
					</Button>
				</div>
			</div>
		);
	}

	renderCopyrightLineSettings(lineId) {
		const readonly = !canUser('write');
		const path = `${this.footerPath}/copyright/${lineId}`;
		const inheritPath = `/navigation/content/moviePageFooter/copyright/${lineId}`;

		return (
			<div class={s.itemEditor}>
				<SimpleField dark label="Text" help="Use [Year] for dynamic year replacement." path={`${path}/name`} inheritPath={`${inheritPath}/name`} inheritType="locale" disabled={readonly} />
				<SimpleField dark label="Link URL" path={`${path}/destination`} placeholder="https://" inheritPath={`${inheritPath}/destination`} inheritType="locale" disabled={readonly} />
				<div class={s.buttonRow}>
					<Button dark text danger disabled={readonly} onClick={() => this.deleteItem(`/copyright/${lineId}`)}>
						Delete line
					</Button>
				</div>
			</div>
		);
	}

	renderSeoMessagingSettings() {
		const autoSeoMessage = getSeoFooterMessaging(this.movie, this.state.thundrData?.copy?.baseCopy);

		return (
			<div class={s.itemEditor}>
				<SimpleField
					dark
					multiline
					rows={3}
					path={`${this.footerPath}/seoMessaging`}
					label="SEO messaging"
					placeholder={autoSeoMessage}
					help="Auto-generated based on page status. Override by entering custom text. Clear to revert to default."
				/>
			</div>
		);
	}

	renderLogosSettings() {
		return (
			<div class={s.section}>
				<h3 class={s.title}>Logos</h3>
				<span>Footer logos can be edited from the movie page media tab.</span>
				<GoToMediaButton dark movie={this.movie} />
			</div>
		);
	}

	renderLinksSettings() {
		const readonly = !canUser('write');
		const footerLinks = sortObjectToList(this.movie?.footer?.links);

		return (
			<div class={s.section}>
				<div class={s.title}>
					<h3>Links</h3>
					<InheritIndicator dark path={`${this.footerPath}/links`} type="locale" />
				</div>
				<CardList
					items={footerLinks}
					disabled={readonly}
					onChange={this.onReorder}
					getItemProps={link => ({
						label: link.name,
						path: `${this.footerPath}/links/${link.id}`,
						inheritPath: `/navigation/content/moviePageFooter/links/${link.id}`,
						inheritType: 'locale',
						'data-path': `/links/${link.id}`,
						onEdit: () => this.props.select(`/footer/links/${link.id}`),
					})}
				/>
				<div class={s.buttonRow}>
					<Button dark primary icon="plus" onClick={() => this.addItem('/links')} disabled={readonly}>
						Add link
					</Button>
				</div>
			</div>
		);
	}

	renderCopyrightsSettings() {
		const readonly = !canUser('write');
		const copyrightLines = sortObjectToList(this.movie?.footer?.copyright);
		const powsterLine = copyrightLines[0];
		const sortableLines = copyrightLines.slice(1);

		return (
			<div class={s.section}>
				<div class={s.title}>
					<h3>Copyright</h3>
					<InheritIndicator dark path={`${this.footerPath}/copyright`} type="locale" />
				</div>
				<CardList
					items={sortableLines}
					onChange={this.onReorder}
					disabled={readonly}
					getItemProps={line => ({
						label: line.name.replace(/\[Year\]/gi, this.copyrightYear),
						path: `${this.footerPath}/copyright/${line.id}`,
						inheritPath: `/navigation/content/moviePageFooter/copyright/${line.id}`,
						inheritType: 'locale',
						'data-path': `/copyright/${line.id}`,
						onEdit: () => this.props.select(`/footer/copyright/${line.id}`),
					})}
				>
					<CardListItem
						label={powsterLine.name.replace(/\[Year\]/gi, this.copyrightYear)}
						path={`${this.footerPath}/copyright/${powsterLine.id}`}
						inheritPath={`/navigation/content/moviePageFooter/copyright/${powsterLine.id}`}
						inheritType="locale"
						disabled
					/>
				</CardList>
				<div class={s.buttonRow}>
					<Button dark primary icon="plus" onClick={() => this.addItem('/copyright')} disabled={readonly}>
						Add line
					</Button>
				</div>
			</div>
		);
	}

	renderUniversalFooterEditor() {
		return (
			<div class={s.footer}>
				<p class={s.helperText}>Changes to the footer will reflect across the movie site</p>

				{this.renderLogosSettings()}
				<hr class={s.divider} />
				{this.renderLinksSettings()}
				<hr class={s.divider} />
				{this.renderCopyrightsSettings()}
				<hr class={s.divider} />

				<h3 class={s.title}>SEO messaging</h3>
				<CardListItem label="Edit SEO messaging" onEdit={() => this.props.select('/footer/seoMessaging')} />
			</div>
		);
	}

	renderSettings() {
		let content;
		let nav = [{ label: 'Footer', path: '/footer' }];

		const activeElement = this.props.activeElement;

		if (activeElement === '/footer') {
			content = this.renderUniversalFooterEditor();
		}

		if (activeElement.startsWith('/footer/links/')) {
			const linkId = activeElement.split('/').pop();
			content = this.renderFooterLinkSettings(linkId);
			nav = [
				{ label: 'Footer', path: '/footer' },
				{ label: 'Link', path: `/footer/links/${linkId}` },
			];
		}

		if (activeElement.startsWith('/footer/copyright/')) {
			const lineId = activeElement.split('/').pop();
			content = this.renderCopyrightLineSettings(lineId);
			nav = [
				{ label: 'Footer', path: '/footer' },
				{ label: 'Copyright line', path: `/footer/copyright/${lineId}` },
			];
		}

		if (activeElement === '/footer/seoMessaging') {
			content = this.renderSeoMessagingSettings();
			nav = [
				{ label: 'Footer', path: '/footer' },
				{ label: 'SEO messaging', path: '/footer/seoMessaging' },
			];
		}

		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}